import { PaymentIntent } from '@stripe/stripe-js'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import localizeCurrency from '../helpers/localizeCurrency'
import { trackCheckoutTermsToggle } from '../helpers/snowplowTracking'
import { Plan, StripePrice } from '../types'

interface TermsCheckboxProps {
  fullPrice: number | undefined
  fullPriceCurrency: string | undefined
  isTermsAccepted: boolean
  paymentIntent: PaymentIntent | undefined
  plan: Plan
  stripePriceFull: StripePrice | undefined
  setIsTermsAccepted: (isTermsAccepted: boolean) => void
  trialPeriodDays?: number
}

export default function TermsCheckbox({
  fullPrice,
  fullPriceCurrency,
  isTermsAccepted,
  paymentIntent,
  plan,
  stripePriceFull,
  setIsTermsAccepted,
  trialPeriodDays,
}: TermsCheckboxProps) {
  const { t } = useTranslation()

  if (!paymentIntent && !trialPeriodDays) return null

  const isOneTime = plan === 'one-time'
  const interval = stripePriceFull?.recurring?.interval
  const intervalCount = stripePriceFull?.recurring?.interval_count ?? 1

  const capitalizeInterval = () => {
    if (isOneTime) return 'OneTime'
    if (!interval) return ''
    return interval.charAt(0).toUpperCase() + interval.slice(1)
  }

  const supportEmail =
    import.meta.env.VITE_SUPPORT_EMAIL ?? 'support@asanarebel.com'

  return (
    <div className="flex gap-2">
      <input
        checked={isTermsAccepted}
        className="mt-1 rounded-sm focus:ring-0 focus:ring-offset-0 focus:outline-none"
        id="terms-checkbox"
        onChange={(event) => {
          const isAccepted = event.target.checked
          setIsTermsAccepted(isAccepted)
          trackCheckoutTermsToggle({
            isAccepted,
            plan,
            priceId: stripePriceFull?.id,
            value: fullPrice,
          })
        }}
        type="checkbox"
      />

      <label
        className="cursor-pointer text-xs leading-relaxed"
        htmlFor="terms-checkbox"
      >
        {trialPeriodDays
          ? t('pages.checkout.payment.termsCheckboxLabelTrial', {
              count: trialPeriodDays,
              email: supportEmail,
              price: localizeCurrency({
                amountInCents: fullPrice,
                currency: fullPriceCurrency,
              }),
            })
          : t(
              `pages.checkout.payment.termsCheckboxLabel${capitalizeInterval()}`,
              {
                count: intervalCount,
                email: supportEmail,
                price: localizeCurrency({
                  amountInCents: fullPrice,
                  currency: fullPriceCurrency,
                }),
              },
            )}{' '}
        <Trans
          components={{
            termsAndConditionsLink: (
              <Link
                className="underline"
                target="_blank"
                to="https://app-studio.ai/terms"
              />
            ),
            privacyPolicyLink: (
              <Link
                className="underline"
                target="_blank"
                to="https://app-studio.ai/privacy"
              />
            ),
          }}
          i18nKey="pages.checkout.payment.termsCheckboxLegalLinks"
        />
      </label>
    </div>
  )
}
